
import {
  defineComponent, ref, computed, onMounted, reactive, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import PageHeader from '@/components/layout/PageHeader.vue';
import Loader from '@/components/layout/Loader.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import ConfigFieldRenderer from '@/views/shop/components/ConfigFieldRenderer.vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';
import { UserState } from '@/composables/User';
import { useConfigWizard } from '@/composables/useConfigWizard';
import type { Integration, ConfigField, ConfigStep } from '@/composables/useIntegrations';

export default defineComponent({
  name: 'IntegrationConfig',

  components: {
    PageHeader,
    Loader,
    SpmButton,
    ConfigFieldRenderer,
  },

  props: {
    // T3 — when hosted in the config modal, the slug comes from a prop rather
    // than the route, the page chrome (PageHeader) is hidden, and exits are
    // emitted instead of navigating.
    slug: { type: String, default: '' },
    asModal: { type: Boolean, default: false },
    // Nom / icône affichés immédiatement dans le header modale (avant que le
    // fetch de config ne renvoie l'intégration) — évite un header vide au 1er rendu.
    initialName: { type: String, default: '' },
    initialIcon: { type: String, default: '' },
  },

  emits: ['close', 'done'],

  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const idShop = UserState.activeShop?.id ?? 0;
    const integrationSlug = props.slug || (route.params.slug as string);

    const loading = ref(true);
    const error = ref('');
    const integration = ref<Integration | null>(null);

    const displayName = ref(props.initialName || window.history.state?.name || '');
    const displayIcon = ref(props.initialIcon || window.history.state?.icon_url || '');
    const displayDescription = ref(window.history.state?.short_description || '');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formData = ref<Record<string, any>>({});
    const isSaving = ref(false);
    const isTesting = ref(false);

    // Wizard reactive state
    const wizardSteps = ref<ConfigStep[]>([]);
    const savedConfigsMap = ref<Map<string, string>>(new Map());
    const isWizardMode = ref(false);

    // Initialize wizard composable with reactive refs
    const wizard = useConfigWizard(wizardSteps, savedConfigsMap);

    // Validation errors
    const fieldErrors = reactive<Record<string, string>>({});

    /**
     * Mappe les erreurs backend `error.errors[key]` (par champ) vers
     * `fieldErrors[key]` afin que le rendu de champ unifié (G7) affiche l'erreur
     * inline sous le bon champ (au lieu d'un seul toast global). Renvoie le texte
     * concaténé pour un éventuel toast complémentaire.
     */
    const applyBackendFieldErrors = (errors: Record<string, unknown>): string => {
      Object.keys(fieldErrors).forEach((k) => { delete fieldErrors[k]; });
      const messages: string[] = [];
      Object.entries(errors || {}).forEach(([key, val]) => {
        const msg = Array.isArray(val) ? val.join('\n') : String(val);
        fieldErrors[key] = msg;
        messages.push(msg);
      });
      return messages.join('\n');
    };

    // Remote data
    const remoteOptions = reactive<Record<string, Array<{ value: string; label: string }>>>({});
    const remoteDataLoading = reactive<Record<string, boolean>>({});
    const remoteDataError = reactive<Record<string, boolean>>({});

    // ---- Computed ----

    const hasConfigFields = computed(() => {
      if (!integration.value?.config) return false;
      return !!(
        (integration.value.config.steps && integration.value.config.steps.length > 0)
        || (integration.value.config.fields && integration.value.config.fields.length > 0)
      );
    });

    const currentWizardStep = computed(() => wizard.currentStep.value);

    const currentFields = computed((): ConfigField[] => {
      const fields = isWizardMode.value && currentWizardStep.value
        ? currentWizardStep.value.fields || []
        : integration.value?.config?.fields || [];
      // Integrator-owned fields (`owner: 'integrator'`) are set by the
      // integrator via the API per shop — never shown to / editable by the merchant.
      return fields.filter((field) => field.owner !== 'integrator');
    });

    const allFields = computed((): ConfigField[] =>
      (integration.value?.config?.fields || []).filter(
        (field) => field.owner !== 'integrator',
      ));

    // ---- Helpers ----

    // T3 — cancel / back. In modal mode we close the modal instead of navigating.
    const goBack = () => {
      if (props.asModal) {
        emit('close');
        return;
      }
      router.push({ name: 'shop.Integrations' });
    };

    // T3 — successful completion. In modal mode we signal the parent (which
    // reloads its lists) instead of navigating.
    const finishConfig = () => {
      if (props.asModal) {
        emit('done');
        return;
      }
      router.push({ name: 'shop.Integrations' });
    };

    const getLocalizedText = (text: string | Record<string, string> | undefined): string => {
      if (!text) return '';
      if (typeof text === 'string') return text;
      return text[locale.value] || text.fr || text.en || '';
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFieldLabel = (field: any): string => getLocalizedText(field.label);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFieldPlaceholder = (field: any): string => getLocalizedText(field.placeholder);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFieldHelpText = (field: any): string => getLocalizedText(field.helpText);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFieldOptions = (field: any): any[] => {
      if (!field.options || !Array.isArray(field.options)) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return field.options.map((o: any) => ({ ...o, label: getLocalizedText(o.label) }));
    };

    // ---- Remote data ----

    const loadRemoteData = async (field: ConfigField) => {
      if (!field.remote) return;
      const { resource, label_field, value_field } = field.remote;
      remoteDataLoading[field.key] = true;
      remoteDataError[field.key] = false;
      try {
        const response = await nestCall(
          'get', NestApiType.CLIENT_API,
          `/integrations/${integrationSlug}/remote-data/${resource}`,
          {}, '', undefined, true, {},
        );
        if (response?.statusCode === 200 && Array.isArray(response.data)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          remoteOptions[field.key] = response.data.map((item: any) => ({
            value: String(item[value_field] || ''),
            label: String(item[label_field] || ''),
          }));
        } else {
          remoteDataError[field.key] = true;
        }
      } catch {
        remoteDataError[field.key] = true;
      } finally {
        remoteDataLoading[field.key] = false;
      }
    };

    const loadRemoteFieldsForCurrentStep = () => {
      for (const field of currentFields.value) {
        if (field.remote && !remoteOptions[field.key]) {
          loadRemoteData(field);
        }
      }
    };

    // Watch step changes to load remote data
    watch(() => wizard.currentStepIndex.value, () => {
      loadRemoteFieldsForCurrentStep();
    });

    // ---- Init ----

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initializeFormData = (configurations: any[] = []): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: Record<string, any> = {};
      const configsMap = new Map<string, string>();

      // Collect all fields
      const fields: ConfigField[] = [];
      if (integration.value?.config?.steps) {
        for (const step of integration.value.config.steps) {
          if (step.fields) fields.push(...step.fields);
        }
      } else if (integration.value?.config?.fields) {
        fields.push(...integration.value.config.fields);
      }

      // Defaults
      fields.forEach((field) => {
        if (field.type === 'checkbox') data[field.key] = false;
        else if (field.type === 'number') data[field.key] = field.validation?.min || 0;
        else if (field.type === 'multiselect') data[field.key] = [];
        else data[field.key] = '';
      });

      // Override with existing
      configurations.forEach((config) => {
        const key = config.config_key || config.key;
        const value = config.config_value || config.value;
        if (key) configsMap.set(key, value || '');
        if (key && value !== undefined && value !== null) {
          const field = fields.find((f) => f.key === key);
          if (field) {
            if (field.type === 'checkbox') data[key] = value === 'true' || value === true;
            else if (field.type === 'number') data[key] = typeof value === 'string' ? parseFloat(value) : value;
            else if (field.type === 'multiselect') {
              try { data[key] = typeof value === 'string' ? JSON.parse(value) : value; } catch { data[key] = []; }
            } else data[key] = value;
          } else {
            data[key] = value;
          }
        }
      });

      formData.value = data;
      savedConfigsMap.value = configsMap;
    };

    const loadIntegration = async () => {
      loading.value = true;
      error.value = '';
      try {
        const response = await nestCall(
          'get', NestApiType.CLIENT_API,
          `/integrations/${integrationSlug}/configuration`,
          {}, '', undefined, true, { shop_id: idShop },
        );

        if (response?.statusCode === 200 && response.data) {
          const { integration: integrationData, configurations } = response.data;
          if (integrationData) {
            integration.value = { ...integrationData, config: integrationData.config_schema };

            if (!displayName.value) {
              displayName.value = integrationData.name;
              displayIcon.value = integrationData.icon_url || '';
              displayDescription.value = integrationData.short_description || integrationData.description || '';
            }

            // Init form data first
            initializeFormData(configurations || []);

            // Then set wizard mode
            if (integration.value?.config?.steps && integration.value.config.steps.length > 0) {
              isWizardMode.value = true;
              wizardSteps.value = integration.value.config.steps;
            }

            // Load remote fields for initial step
            loadRemoteFieldsForCurrentStep();
          } else {
            error.value = t('integrations.errors.notFound');
          }
        } else {
          error.value = t('integrations.errors.notFound');
        }
      } catch (err) {
        console.error('Error loading integration:', err);
        error.value = t('integrations.errors.loadConfig');
      } finally {
        loading.value = false;
      }
    };

    // ---- Save ----

    const saveCurrentStepFields = async (): Promise<boolean> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataToSave: Record<string, any> = {};
      for (const field of currentFields.value) {
        let val = formData.value[field.key];
        if (field.type === 'multiselect' && Array.isArray(val)) val = JSON.stringify(val);
        dataToSave[field.key] = val;
      }

      const response = await nestCall(
        'post', NestApiType.CLIENT_API,
        `/integrations/${integrationSlug}/configuration`,
        {}, '', dataToSave, true, {},
      );

      if (response?.error?.errors) {
        const msgs = applyBackendFieldErrors(response.error.errors);
        await showToastError(msgs);
        return false;
      }
      if (response?.statusCode === 200 || response?.statusCode === 201) {
        // Update local map
        for (const [key, val] of Object.entries(dataToSave)) {
          savedConfigsMap.value = new Map(savedConfigsMap.value).set(key, String(val));
        }
        return true;
      }
      await showToastError(response?.error?.message || t('integrations.errors.configure'));
      return false;
    };

    const testConnectionForStep = async (): Promise<boolean> => {
      isTesting.value = true;
      try {
        const response = await nestCall(
          'post', NestApiType.CLIENT_API,
          `/integrations/${integrationSlug}/test-connection`,
          {}, '', {}, true, {},
        );
        if (response?.statusCode === 200 && response.data?.success) {
          const step = currentWizardStep.value;
          if (step?.on_complete?.success_message) {
            await showToastSuccess(getLocalizedText(step.on_complete.success_message));
          } else {
            await showToastSuccess(t('integrations.config.connectionSuccess'));
          }
          if (step) {
            savedConfigsMap.value = new Map(savedConfigsMap.value).set(`__step:${step.key}:validated`, 'true');
          }
          return true;
        }
        const step = currentWizardStep.value;
        // T4 — never surface the connector's RAW error code (e.g.
        // "connection_failed") to the merchant. Priority: integrator-defined
        // message (manifest `on_complete.error_message`, localized) → generic
        // translated message. The raw code is logged for debugging only.
        if (response?.data?.error) {
          // eslint-disable-next-line no-console
          console.warn(`[integration:${integrationSlug}] test_connection failed:`, response.data.error);
        }
        const errorMsg = (step?.on_complete?.error_message
          ? getLocalizedText(step.on_complete.error_message)
          : '')
          || t('integrations.config.connectionFailed');
        await showToastError(errorMsg);
        return false;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[integration:${integrationSlug}] test_connection error:`, e);
        const step = currentWizardStep.value;
        const errorMsg = (step?.on_complete?.error_message
          ? getLocalizedText(step.on_complete.error_message)
          : '')
          || t('integrations.config.connectionFailed');
        await showToastError(errorMsg);
        return false;
      } finally {
        isTesting.value = false;
      }
    };

    const validateCurrentFields = (): boolean => {
      // Clear previous errors
      Object.keys(fieldErrors).forEach((key) => {
        delete fieldErrors[key];
      });

      let valid = true;
      currentFields.value.filter((field) => field.required).forEach((field) => {
        const val = formData.value[field.key];
        if (field.type === 'multiselect') {
          if (!Array.isArray(val) || val.length === 0) {
            fieldErrors[field.key] = t('integrations.config.fieldRequired', { field: getFieldLabel(field) });
            valid = false;
          }
        } else if (field.type === 'checkbox') {
          // Checkbox required means it must be checked
          // Usually not the case, skip
        } else if (val === undefined || val === null || val === '') {
          fieldErrors[field.key] = t('integrations.config.fieldRequired', { field: getFieldLabel(field) });
          valid = false;
        }
      });
      return valid;
    };

    const handleSubmit = async () => {
      if (!integration.value) return;

      if (!validateCurrentFields()) return;

      isSaving.value = true;
      try {
        const saved = await saveCurrentStepFields();
        if (!saved) return;

        if (currentWizardStep.value?.on_complete?.action === 'test_connection') {
          const ok = await testConnectionForStep();
          if (!ok) return;
        }

        if (!wizard.isLastStep.value) {
          wizard.next();
        } else {
          await showToastSuccess(t('integrations.success.configured', { name: integration.value.name }));
          setTimeout(() => finishConfig(), 800);
        }
      } catch {
        await showToastError(t('integrations.errors.configure'));
      } finally {
        isSaving.value = false;
      }
    };

    const handleSubmitSimple = async () => {
      if (!integration.value) return;
      isSaving.value = true;
      try {
        // Sérialisation multiselect unifiée avec le mode wizard (G7) : les
        // valeurs multiselect sont sérialisées en JSON avant l'envoi.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dataToSave: Record<string, any> = {};
        for (const field of allFields.value) {
          let val = formData.value[field.key];
          if (field.type === 'multiselect' && Array.isArray(val)) val = JSON.stringify(val);
          dataToSave[field.key] = val;
        }
        const response = await nestCall(
          'post', NestApiType.CLIENT_API,
          `/integrations/${integrationSlug}/configuration`,
          {}, '', dataToSave, true, {},
        );
        if (response?.error?.errors) {
          const msgs = applyBackendFieldErrors(response.error.errors);
          await showToastError(msgs);
        } else if (response?.statusCode === 200 || response?.statusCode === 201) {
          await showToastSuccess(t('integrations.success.configured', { name: integration.value.name }));
          setTimeout(() => finishConfig(), 800);
        } else {
          await showToastError(response?.error?.message || t('integrations.errors.configure'));
        }
      } catch {
        await showToastError(t('integrations.errors.configure'));
      } finally {
        isSaving.value = false;
      }
    };

    onMounted(() => loadIntegration());

    return {
      t,
      loading,
      error,
      integration,
      displayName,
      displayIcon,
      displayDescription,
      formData,
      isSaving,
      isTesting,
      isWizardMode,
      wizardSteps,
      wizard,
      currentWizardStep,
      currentFields,
      allFields,
      hasConfigFields,
      fieldErrors,
      remoteOptions,
      remoteDataLoading,
      remoteDataError,
      getLocalizedText,
      getFieldLabel,
      getFieldPlaceholder,
      getFieldHelpText,
      getFieldOptions,
      loadIntegration,
      loadRemoteData,
      handleSubmit,
      handleSubmitSimple,
      goBack,
    };
  },
});
